import {
  applyColorHandleAction,
  applyDeleteAction,
} from './../ModeBar/actions';
import { ConfiguratorMode } from './../ModeBar/reducer';
import {
  addToShelvesSelectionAction,
  shelvesSelectionAction,
} from './../Configurator/actions/selection';
import { selectCabinetFromIdAction } from './../Configurator/actions/furniture';
import { applyPaintColor } from './../PaintPanel/actions';
import {
  WHITE_COLOR,
  GALVA_COLOR,
  INOX_COLOR,
  BLACK_COLOR,
} from './../../lib/modelBuilder/constants';
import { DoorHandle } from './../../lib/modelBuilder/pieces/DoorHandle';
import {
  CustomPiece,
  KewloxPieceType,
} from 'lib/modelBuilder/pieces/CustomPiece';
import { Door } from 'lib/modelBuilder/pieces/Door';
import { Drawer } from 'lib/modelBuilder/pieces/Drawer';
import { Cabinet } from './../../lib/modelBuilder/entities/Cabinet';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { Rack } from 'lib/modelBuilder/entities/Rack';
import { CompleteIntermediateShelf } from 'lib/modelBuilder/entities/CompleteIntermediateShelf';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import * as constantsViewer from './constants';
import { computeFov } from './utils';
import { Corner } from '../../lib/modelBuilder/pieces/Corner';
import { UpperShelf } from './components/pieces/UpperShelf';

let _global_shelf_clientX = 0;
let _global_shelf_clientY = 0;
const _global_shelf_mouse_max = 3;

export function onPointerDown(e: PointerEvent) {
  e.stopPropagation();
  _global_shelf_clientX = e.clientX;
  _global_shelf_clientY = e.clientY;
}

export function onPointerUp(
  e: PointerEvent,
  callback: (e: PointerEvent) => void,
) {
  if (
    Math.abs(_global_shelf_clientX - e.clientX) <= _global_shelf_mouse_max &&
    Math.abs(_global_shelf_clientY - e.clientY) <= _global_shelf_mouse_max
  )
    callback(e);
}

export interface PositionShelf {
  shelf: Shelf;
  position: number;
}

export interface PositionRack {
  rack: Rack;
  position: number;
}

export interface PositionDrawer {
  drawer: CustomPiece<Drawer>;
  position: number;
  level: number;
}

export interface PositionInterShelf {
  interShelf: CompleteIntermediateShelf;
  position: number;
}

export interface PositionCabinet {
  cabinet: Cabinet;
  position: number[];
}

export function getCabinetsWithPositions(
  furniture: Furniture,
  selectedCabinetId: string,
): PositionCabinet[] {
  const nbCabinets = furniture.quantity;
  const result: PositionCabinet[] = [];
  const cabinetsWidth = furniture.width;
  const cabinetsMaxHeight = furniture.height;
  const cabinetsMaxDepth = furniture.depth;

  let xOffset = 0;
  for (const cabinetEntry of furniture.sortedCabinets) {
    const cabinet = cabinetEntry.cabinet;
    if (selectedCabinetId === null || selectedCabinetId === cabinet.id) {
      let xPos = 0;
      let yPos = 0;
      let zPos = 0;
      // Test for alignement on x axis
      //yPos = cabinet.height / 2;
      if (selectedCabinetId === null) {
        xPos = xOffset - (cabinetsWidth - 999) / 2 + (cabinet.width - 999) / 2;
        xOffset += cabinet.width;

        if (cabinet.height < cabinetsMaxHeight) {
          yPos = -(cabinetsMaxHeight - cabinet.height) / 2;
        }

        if (cabinet.depth < cabinetsMaxDepth) {
          zPos = -(cabinetsMaxDepth - cabinet.depth) / 2;
        }
      }
      result.push({ cabinet: cabinet, position: [xPos, yPos, zPos] });
    }
  }

  return result;
}

export function getShelvesWithPositions(cabinet: Cabinet): PositionShelf[] {
  const nbShelves = cabinet.numberOfShelves;
  const result: PositionShelf[] = [];
  let currentPosition = cabinet.bottomSupportHeight + cabinet.groundOffset;
  for (let i = 0; i < nbShelves; i++) {
    const shelf = cabinet.shelf(i);
    result.push({ shelf: shelf, position: currentPosition });
    currentPosition += shelf.realHeight;
  }

  return result;
}

export function getRacksWithPositions(shelf: Shelf): PositionRack[] {
  const nbRacks = shelf.racks.length;
  const result: PositionRack[] = [];
  let currentPosition = 0;
  for (let i = 0; i < nbRacks; i++) {
    const rack = shelf.rack(i);
    if (rack) {
      result.push({ rack: rack, position: currentPosition });
      currentPosition += rack.realHeight;
    }
  }

  return result;
}

export function getDrawersWithPositions(shelf: Shelf): PositionDrawer[] {
  const nbDrawers = shelf.drawers.length;
  const result: PositionDrawer[] = [];
  let currentPosition = 0;
  for (let i = 0; i < nbDrawers; i++) {
    const drawer = shelf.getDrawer(i);
    if (drawer) {
      result.push({ drawer: drawer, position: currentPosition, level: i });
      currentPosition += drawer.height;
    }
  }

  return result;
}

export function getInterShelvesWithPositions(
  shelf: Shelf,
): PositionInterShelf[] {
  const nbIS = shelf.intermediatesShelves.length;
  const result: PositionInterShelf[] = [];
  let currentPosition = 0;
  for (let i = 0; i < nbIS; i++) {
    const interShelf = shelf.getIntermediateShelf(i);
    if (interShelf) {
      currentPosition += interShelf.supports[0].height;
      result.push({ interShelf: interShelf, position: currentPosition });
      currentPosition += 8;
      if (
        interShelf.interShelf.material === 'I' ||
        interShelf.interShelf.material === 'G'
      )
        currentPosition -= 5;
    }
  }

  return result;
}

export function getMaterialData(
  piece: CustomPiece<KewloxPieceType> | DoorHandle,
  textures: any,
) {
  let texture = null;
  let color = piece.color ? piece.color.hexaNum : 0xffffff;
  let opacity = piece.color ? piece.color.opacity : 1;

  let specular = 0x919191;
  let shininess = 4.92458;

  if (piece.color) {
    if (piece.color.reference === INOX_COLOR) {
      color = constantsViewer.MATERIAL_ALU_COLOR;
      specular = constantsViewer.MATERIAL_ALU_SPECULAR;
      shininess = constantsViewer.MATERIAL_ALU_SHININESS;
      texture = textures.textureInoxLoaded;
    } else if (piece.color.reference === GALVA_COLOR) {
      color = 0xffffff;
      specular = 0x111111;
      shininess = 30;
      texture = textures.textureGalvaLoaded;
    } else if (piece.color.reference === WHITE_COLOR) {
      // color = 0xeeeeee;
      specular = 0x919191;
      shininess = 4.92458;
    } else if (piece.color.reference === BLACK_COLOR) {
      // color = 0x1f1f1f;
      specular = 0x282828;
      shininess = 6.9644;
    }
  }

  if (piece instanceof CustomPiece && piece.hasMaterial()) {
    if (piece.material === 'I') {
      texture = textures.textureMirrorLoaded;
      color = constantsViewer.MATERIAL_MIRROR2_COLOR;
      specular = constantsViewer.MATERIAL_MIRROR2_SPECULAR;
      shininess = constantsViewer.MATERIAL_MIRROR2_SHININESS;
    } else if (piece.material === 'A') {
      texture = textures.textureAluLoaded;
      color = constantsViewer.MATERIAL_ALU_COLOR;
      specular = constantsViewer.MATERIAL_ALU_SPECULAR;
      shininess = constantsViewer.MATERIAL_ALU_SHININESS;
    } else if (piece.material === 'M' && piece.color.reference === 'H5474') {
      texture = textures.textureWhiteBeechLoaded;
    } else if (
      (piece.material === 'M' && piece.color.reference === 'BRUT') ||
      (piece.isDrawer() &&
        (piece.drawerHandlesType === 'E' || piece.drawerHandlesType === 'EP'))
    ) {
      texture = textures.textureMdfNaturalLoaded;
      color = constantsViewer.MATERIAL_MDFNATURAL_COLOR;
    } else if (piece.material === 'G') {
      texture = textures.textureGlassLoaded;
      color = constantsViewer.MATERIAL_GLASS_COLOR;
      specular = constantsViewer.MATERIAL_GLASS_SPECULAR;
      shininess = constantsViewer.MATERIAL_GLASS_SHININESS;
      opacity = constantsViewer.MATERIAL_GLASS_OPACITY;
    } else if (piece.material === 'O') {
      opacity = 1;
    }
  }

  return {
    texture: texture,
    color: color,
    opacity: opacity,
    specular: specular,
    shininess: shininess,
    isCorner:
      piece instanceof CustomPiece && piece.base instanceof Corner
        ? true
        : false,
    isUpperShelf:
      piece instanceof CustomPiece && piece.base instanceof UpperShelf
        ? true
        : false,
  };
}

export function startAnimateForSelectionPiece(
  materialData: any,
  setSpringOpacity: any,
) {
  return setInterval(
    () =>
      setSpringOpacity({
        opacity: 0,
        config: { duration: 400 },
        onRest: () => {
          setSpringOpacity({
            opacity: materialData.opacity,
            config: { duration: 250 },
          });
        },
      }),
    3650,
  );
}

export function computeConfiguratorFov(
  widthScene: number,
  heightScene: number,
  aspect: number,
) {
  let fixedMarginH = 200,
    fixedMarginV = 200;

  if (heightScene < 700) fixedMarginV = 450;
  else if (heightScene < 1100) fixedMarginV = 300;
  // Add margin (can be multiple because of furniture / cabinet inclinaison)
  const height = Math.max(heightScene + fixedMarginV, heightScene * 1.2);
  const width = Math.max(widthScene + fixedMarginH, widthScene * 1.2);

  // Distance is default camera z
  const distance = constantsViewer.DEFAULT_CAMERA_POSITION[2];

  return computeFov(width, height, aspect, distance);
}

export function manageOnClickShelf(
  e: PointerEvent,
  multiCabinets: boolean,
  cabinetId: string,
  dispatch: any,
  configuratorMode: ConfiguratorMode,
  shelfLevel: number,
) {
  if (configuratorMode !== ConfiguratorMode.selector) return;

  e.stopPropagation();

  if (multiCabinets) dispatch(selectCabinetFromIdAction(cabinetId));
  else {
    if (e.shiftKey || e.ctrlKey) {
      dispatch(addToShelvesSelectionAction(shelfLevel));
    } else {
      dispatch(shelvesSelectionAction([shelfLevel]));
    }
  }
}

export function manageOnClickComponent(
  e: PointerEvent,
  unselected: boolean,
  component: CustomPiece<KewloxPieceType>,
  cabinetId: string,
  dispatch: any,
  configuratorMode: ConfiguratorMode,
  shelfLevel?: number,
  rackLevel?: number,
  interShelfLevel?: number,
) {
  if (unselected || configuratorMode === ConfiguratorMode.selector) return;

  e.stopPropagation();

  switch (configuratorMode) {
    case ConfiguratorMode.paint:
      dispatch(
        applyPaintColor(
          component,
          cabinetId,
          shelfLevel,
          rackLevel,
          interShelfLevel,
        ),
      );
      break;
    case ConfiguratorMode.handles:
      if (component.base instanceof Door || component.base instanceof Drawer)
        dispatch(
          applyColorHandleAction(
            component,
            cabinetId,
            shelfLevel,
            rackLevel,
            interShelfLevel,
          ),
        );
      break;
    case ConfiguratorMode.delete:
      dispatch(
        applyDeleteAction(
          component,
          cabinetId,
          shelfLevel,
          rackLevel,
          interShelfLevel,
        ),
      );
      break;
    default:
      break;
  }
}
